<template>
  <div class="provider-filter" :class="[isMobileLayout ? 'ml-16' : '']">
    <el-collapse v-model="activeNames" accordion>
      <el-collapse-item name="country">
        <template slot="title">
          <div class="subtitle1">
            {{ countriesLabel }}
          </div>
        </template>

        <Scrollable
          id="tree-root"
          class="tree__root ml provider-countries-filter"
        >
          <el-tree
            ref="tree"
            node-key="id"
            :empty-text="'empty.text'"
            :data="treeData"
            :props="treeProps"
            :class="'tree_layout'"
            :default-checked-keys="treeDefaultCheckedKeys"
            highlight-current
            :default-expanded-keys="[0]"
            show-checkbox
            @check-change="handleCheckChange"
          />
        </Scrollable>

        <!-- <el-divider /> -->
      </el-collapse-item>
    </el-collapse>
  </div>
</template>

<script>
import { cloneDeep, forEach, get, isEmpty } from "@/utils/lodash";

import layoutUtils from "@/utils/layout-utils";
import Scrollable from "@/components/scrollable.vue";

export default {
  components: {
    Scrollable,
  },
  props: {
    filters: {
      type: Object,
      default: () => ({
        attributes: [],
        countries: [],
        regions: [],
      }),
    },
    attributesModel: {
      type: Array,
      default: () => [],
    },
    countriesModel: {
      type: Array,
      default: () => [],
    },
    regionsModel: {
      type: Array,
      default: () => [],
    },
    filterReset: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      clonedAttributesModel: this.attributesModel,
      clonedCountriesModel: this.countriesModel,
      clonedRegionsModel: this.regionsModel,
      modelName: {
        attribute: "attribute",
        country: "country",
        region: "region",
      },
      treeProps: {
        label: "name",
        children: "regions",
        disabled: (data) => {
          const regions = get(data, "regions");
          return regions && regions.length === 0;
        },
      },
      treeDefaultCheckedKeys: [],
      activeNames: "country",
    };
  },
  computed: {
    countriesLabel() {
      return this.$i18n.t("providers.filters.label.countryRegion");
    },
    selectedFilters() {
      const selectedFilters = [];

      forEach(this.clonedAttributesModel, (clonedAttributeValue) => {
        const attr = this.filters.attributes.find(
          (attribute) => attribute.value === clonedAttributeValue
        );
        selectedFilters.push({
          ...attr,
          code: attr.value,
          name: attr.value,
          modelName: this.modelName.attribute,
        });
      });

      forEach(this.clonedCountriesModel, (clonedCountryCode) => {
        selectedFilters.push({
          ...this.filters.countries.find(
            (country) => country.code === clonedCountryCode
          ),
          modelName: this.modelName.country,
        });
      });

      forEach(this.clonedRegionsModel, (cloneRegionCode) => {
        selectedFilters.push({
          ...this.filters.regions.find(
            (region) => region.code === cloneRegionCode
          ),
          modelName: this.modelName.region,
        });
      });

      return selectedFilters;
    },
    treeData() {
      // Helper: safe compare by "name" (null/undefined go last)
      const safeCompareName = (a, b) => {
        const na = a?.name;
        const nb = b?.name;

        if (na == null && nb == null) return 0;
        if (na == null) return 1;   // a after b
        if (nb == null) return -1;  // b after a

        return String(na).localeCompare(String(nb));
      };

      // Clone first (avoid mutating this.filters.countries)
      const clonedCountries = cloneDeep(this.filters?.countries || []);

      // Sort countries in ASCENDING order
      clonedCountries.sort(safeCompareName);

      // Sort regions within each country in ASCENDING order
      forEach(clonedCountries, (country) => {
        if (!isEmpty(country?.regions)) {
          country.regions.sort(safeCompareName);
        }
      });

      const root = {
        id: 0,
        name: this.$i18n.t("providers.filters.label.all"),
        regions: clonedCountries,
        category: "root",
      };

      forEach(clonedCountries, (country) => {
        country.originalId = country.id;
        country.id = 10000 + country.id;
        country.category = "country";
        country.parent = root;

        if (!isEmpty(country?.regions)) {
          forEach(country.regions, (region) => {
            region.originalId = region.id;
            region.category = "region";
            region.parent = country;
          });
        }
      });

      return [root];
    },
    isFilterReset() {
      return this.filterReset;
    },
    isMobileLayout() {
      return layoutUtils.isMobileLayout(this.$mq);
    },
  },
  watch: {
    clonedAttributesModel() {
      this.setFiltersToQuery();
      this.emitFilterChange();
    },
    clonedCountriesModel() {
      this.setFiltersToQuery();
      this.emitFilterChange();
    },
    clonedRegionsModel() {
      this.setFiltersToQuery();
      this.emitFilterChange();
    },
    isFilterReset() {
      if (this.filterReset) {
        this.$refs.tree.setCheckedKeys([]);
        this.$emit("filter-reset");
      }
    },
  },
  mounted() {
    const checkedKeys = [];

    if (this.$route.query.a && this.$route.query.a.length > 0)
      this.treeDefaultCheckedKeys = [this.$route.query.a];
    if (this.$route.query.r && this.$route.query.r.length > 0) {
      const regionsParam = Array.isArray(this.$route.query.r) 
        ? this.$route.query.r 
        : [this.$route.query.r];

      regionsParam.forEach(regionCode => {
        // Convert to number if needed
        const code = Number(regionCode);
        if (!isNaN(code)) {
         checkedKeys.push(code);
        }
      });

      this.treeDefaultCheckedKeys = checkedKeys;
    }
    if (this.$route.query.c && this.$route.query.c.length > 0)
      this.treeDefaultCheckedKeys = [this.$route.query.c];
  },
  methods: {
    setFiltersToQuery() {
      this.$router
        .push({
          query: {
            ...this.$route.query,
            a: this.clonedAttributesModel,
            c: this.clonedCountriesModel,
            r: this.clonedRegionsModel,
          },
        })
        .catch(() => {});
    },
    emitFilterChange() {
      this.$emit("filter-change", {
        attributes: this.clonedAttributesModel,
        countries: this.clonedCountriesModel,
        regions: this.clonedRegionsModel,
        req: true,
      });
    },
    updateFilterElements() {
      const regionsModel = [];

      forEach(this.$refs.tree.getCheckedNodes(), (node) => {
        if (node) {
          switch (node.category) {
            case this.modelName.attribute:
              break;
            case this.modelName.country:
              break;
            case this.modelName.region:
              if (regionsModel.findIndex((code) => code === node.id) === -1) {
                regionsModel.push(node.id);
              }
              break;
            default:
              break;
          }
        }
      });

      this.clonedRegionsModel = regionsModel;
    },
    handleCheckChange(data) {
      if (data.name !== this.$i18n.t("providers.filters.label.all")) {
        this.updateFilterElements();
      }
    },
    initTreeDefaultCheckedKeys() {
      const defaultSelectedIdentifiers = [];
      if (isEmpty(this.clonedRegionsModel)) {
        defaultSelectedIdentifiers.push(0);
      } else {
        forEach(this.clonedRegionsModel, (regionId) => {
          defaultSelectedIdentifiers.push(regionId);
        });
      }
      this.treeDefaultCheckedKeys = defaultSelectedIdentifiers;
    },
  },
};
</script>

<style lang="scss">
@import "@/assets/variables.scss";

.el-tree {
  .el-tree-node__label {
    color: #272421 !important;
  }
  & span {
    font-family: $font-family-bold-book;
    font-weight: $font-weight-bold-book;

    @media screen and (min-width: $media-sm) {
      font-size: 13px;
      letter-spacing: 0.25px;
    }

    @media screen and (max-width: $media-sm) {
      font-size: 13px;
      letter-spacing: 0.25px;
    }
  }
}
.el-checkbox__input.is-checked {
  & .el-checkbox__inner {
    background-color: $color-primary !important;
    border-color: $color-primary !important;
  }
}
.el-checkbox__input.is-checked .el-checkbox__inner,
.el-checkbox__input.is-indeterminate .el-checkbox__inner {
  background-color: $color-primary !important;
  border-color: $color-primary !important;
}
.el-drawer__header {
  text-align: left;
  margin-bottom: 12px !important;
  padding-left: 16px !important;
  font-family: $font-family-bold;
  font-weight: $font-weight-bold;
  color: #333333 !important;
  font-size: 18px !important;
}
.tree__root {
  height: 750px;
  overflow-y: scroll !important;
}
.tree_layout {
  margin-left: -24px;
  width: 250%;
}
</style>
